import { useContext } from "react";
import LoginImage from "../../assets/img/skor_login_img.png";
import Logo from "../../assets/img/logo.svg";
import useTranslator from "../../hooks/useTranslator";
import AuthContext from "../../context/auth/AuthContext";
import "./onboardingProgress.css";
import "./guarantorOnboardingProgress.css";

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="m4.5 10.2 3.3 3.3 7.7-7.7" />
  </svg>
);

export default function GuarantorOnboardingProgress({
  step,
  guarantorName,
  originalUser,
  guarantors,
}) {
  const { _t } = useTranslator();
  const { user } = useContext(AuthContext);

  const activeName =
    guarantorName || user?.fname || _t("guarantor", "Guarantor");
  const displayGuarantors = guarantors?.length
    ? guarantors
    : [{ name: activeName, status: "active" }];

  // Map OnboardingStep to sidebar steps like image: Details(1) Identity(2) Documents(3-5) Financial(6)
  const profileSteps = [
    { id: "details", label: _t("onboardingDetails", "Details"), step: 1 },
    { id: "identity", label: _t("onboardingIdentity", "Identity"), step: 2 },
    { id: "documents", label: _t("onboardingDocuments", "Documents"), step: 3 },
    {
      id: "financial",
      label: _t("onboardingFinancialStudy", "Financial study"),
      step: 6,
      badge: _t("onboardingRecommended", "Recommended"),
    },
  ];
  const activeProfileIndex = (() => {
    if (step === 1) return 0;
    if (step === 2) return 1;
    if (step >= 3 && step <= 5) return 2;
    if (step === 6) return 3;
    return 0;
  })();

  const youCompleted =
    !!originalUser?.profile_completed || !!originalUser?.step;

  return (
    <aside
      className="onboarding-progress guarantor-onboarding-progress"
      aria-label={_t("guarantorProgress", "Guarantor progress")}
    >
      <img className="onboarding-progress__image" src={LoginImage} alt="" />
      <div className="onboarding-progress__shade" aria-hidden="true" />
      <div className="onboarding-progress__content">
        <img className="onboarding-progress__logo" src={Logo} alt="SKOR" />

        <p className="guarantor-progress__eyebrow">
          {_t("guarantorProgressEyebrowApplication", "THE APPLICATION")}
        </p>
        <ul className="guarantor-progress__people">
          <li className="guarantor-progress__person is-complete">
            <span className="guarantor-progress__avatar">
              {(originalUser?.fname || "Y").charAt(0).toUpperCase()}
            </span>
            <div>
              <strong>{_t("you", "You")}</strong>
              <span>{_t("profileCompleted", "Profile completed")}</span>
            </div>
            <span className="guarantor-progress__check">
              <CheckIcon />
            </span>
          </li>
          {displayGuarantors.map((g) => {
            const isActive = g.name === activeName;
            const label = isActive
              ? _t(
                  "youAreCompletingTheirProfile",
                  "You're completing their profile"
                )
              : g.completed
                ? _t("profileCompleted", "Profile completed")
                : _t("notStarted", "Not started");
            return (
              <li
                key={g.name}
                className={`guarantor-progress__person ${isActive ? "is-active" : ""} ${g.completed ? "is-complete" : ""}`}
              >
                <span className="guarantor-progress__avatar">
                  {g.name.charAt(0).toUpperCase()}
                </span>
                <div>
                  <strong>{g.name}</strong>
                  <span>{label}</span>
                </div>
                {g.completed && (
                  <span className="guarantor-progress__check">
                    <CheckIcon />
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <p className="guarantor-progress__eyebrow">
          {_t("guarantorProfileEyebrow", "{{name}}'S PROFILE", undefined, {
            name: activeName.toUpperCase(),
          })}
        </p>
        <ol className="onboarding-progress__steps guarantor-progress__steps">
          {profileSteps.map((s, idx) => {
            const isActive = idx === activeProfileIndex;
            const isComplete = idx < activeProfileIndex;
            return (
              <li
                key={s.id}
                className={`onboarding-progress__step ${isActive ? "is-active" : isComplete ? "is-complete" : ""}`}
                aria-current={isActive ? "step" : undefined}
              >
                <span className="onboarding-progress__marker">
                  {isComplete ? <CheckIcon /> : idx + 1}
                </span>
                <div className="onboarding-progress__step-content">
                  <div className="onboarding-progress__title-row">
                    <span className="onboarding-progress__title">
                      {s.label}
                    </span>
                    {s.badge && (
                      <span className="onboarding-progress__badge">
                        {s.badge}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>

        <div className="onboarding-progress__mobile">
          <img
            className="onboarding-progress__mobile-logo"
            src={Logo}
            alt="SKOR"
          />
          <div className="onboarding-progress__mobile-process">
            <div
              className="onboarding-progress__mobile-track"
              style={{
                left: `${50 / profileSteps.length}%`,
                right: `${50 / profileSteps.length}%`,
              }}
              aria-hidden="true"
            >
              <span
                style={{
                  width: `${(activeProfileIndex / (profileSteps.length - 1)) * 100}%`,
                }}
              />
            </div>
            <ol
              className="onboarding-progress__mobile-steps"
              style={{
                gridTemplateColumns: `repeat(${profileSteps.length},1fr)`,
              }}
            >
              {profileSteps.map((s, i) => (
                <li
                  key={s.id}
                  className={`${i === activeProfileIndex ? "is-active" : ""} ${i < activeProfileIndex ? "is-complete" : ""}`}
                >
                  <span>
                    {i < activeProfileIndex ? (
                      <CheckIcon />
                    ) : (
                      <span aria-hidden="true" />
                    )}
                  </span>
                </li>
              ))}
            </ol>
            <div
              className="onboarding-progress__mobile-label"
              style={{
                gridTemplateColumns: `repeat(${profileSteps.length},1fr)`,
              }}
            >
              <strong style={{ gridColumn: activeProfileIndex + 1 }}>
                {profileSteps[activeProfileIndex].label}
              </strong>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
